import React, { useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"
import type { UiWalletAccount } from "@wallet-standard/react"
import { UserStatus } from "~/__generated__/graphql"
import { useViewerMaybe } from "~/auth/use-viewer"
import { contactsPath, onboardingVerifyEmailPath } from "~/common/paths"
import { SolanaWalletConnect } from "~/solana/components/SolanaWalletConnect"
import { Button } from "~/ui/button"
import { useToast } from "~/ui/use-toast"
import { InvitationCard } from "./invitation-card"
import { useInvitationLoader } from "./use-invitation-loader"
import { useInvitation, useAcceptInvitation } from "./invitation-context"
import { LoginScreenLayout } from "~/ui/login-screen-layout"
import Text from "~/ui/typography"

export const InvitationLoginScreen: React.FC = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { token } = useParams<{ token: string }>()
  const { loading, error } = useInvitationLoader(token || null)
  const { invitation, hasInvitation } = useInvitation()
  const {
    viewer,
    result: { refetch: refetchViewer },
  } = useViewerMaybe()

  const { acceptInvitation } = useAcceptInvitation({ redirectOnError: true })

  // Handle accepting invitation for already authenticated users
  const handleAcceptInvitation = useCallback(async () => {
    await acceptInvitation()
  }, [acceptInvitation])

  const handleSuccess = useCallback(
    async (_account: UiWalletAccount | undefined) => {
      // Refetch viewer to get current user status
      const viewerResult = await refetchViewer()
      const currentUser = viewerResult.data?.viewer

      if (!currentUser) {
        toast({
          title: "Error",
          description: "Failed to load user information",
          variant: "destructive",
        })
        return
      }

      // Check if user needs onboarding
      if (currentUser.userStatus !== UserStatus.Active) {
        // New user - redirect to onboarding starting with email verification
        toast({
          title: "Welcome!",
          description: "Please complete your account setup.",
        })
        navigate(onboardingVerifyEmailPath({}))
        return
      }

      // Existing active user - accept invitation directly
      if (hasInvitation && invitation?.id) {
        await acceptInvitation()
      } else {
        // No invitation - just go to contacts
        navigate(contactsPath({}))
      }
    },
    [refetchViewer, navigate, toast, hasInvitation, acceptInvitation]
  )

  if (loading) {
    return (
      <LoginScreenLayout
        headerSubtitle="Loading"
        headerTitle="Loading invitation..."
        testId="invitation-login-screen-loading"
      >
        <div className="flex items-center justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
        </div>
      </LoginScreenLayout>
    )
  }

  if (error || !hasInvitation) {
    return (
      <LoginScreenLayout
        headerSubtitle="Invalid Invitation"
        headerTitle="Invitation not found"
        testId="invitation-login-screen-error"
      >
        <Text className="text-sm text-gray-600">
          This invitation link is invalid or has expired. Please check the link or contact the
          person who invited you.
        </Text>
      </LoginScreenLayout>
    )
  }

  // Handle already authenticated users
  if (viewer && viewer.userStatus === UserStatus.Active) {
    return (
      <LoginScreenLayout
        headerSubtitle="Accept Invite"
        headerTitle="Accept invitation"
        topContent={invitation && <InvitationCard invitation={invitation} />}
        testId="invitation-login-screen-authenticated"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            You're already signed in. Click the button below to accept this invitation.
          </p>
          <Button onClick={handleAcceptInvitation} disabled={!invitation?.id} className="w-full">
            Accept Invitation
          </Button>
        </div>
      </LoginScreenLayout>
    )
  }

  // Handle authenticated users who need onboarding
  if (viewer && viewer.userStatus !== UserStatus.Active) {
    return (
      <LoginScreenLayout
        headerSubtitle="Complete Setup"
        headerTitle="Complete your account setup"
        topContent={invitation && <InvitationCard invitation={invitation} />}
        testId="invitation-login-screen-onboarding"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Please complete your account setup to accept this invitation.
          </p>
          <Button onClick={() => navigate(onboardingVerifyEmailPath({}))} className="w-full">
            Complete Setup
          </Button>
        </div>
      </LoginScreenLayout>
    )
  }

  // Handle non-authenticated users
  return (
    <LoginScreenLayout
      headerSubtitle="Accept Invite"
      headerTitle="Sign in with your wallet"
      topContent={invitation && <InvitationCard invitation={invitation} />}
      testId="invitation-login-screen"
    >
      <SolanaWalletConnect mode="signin" onSuccess={handleSuccess} />
    </LoginScreenLayout>
  )
}
